import { BigNumber } from '@ethersproject/bignumber'
import { Contract } from '@ethersproject/contracts'
import type { Provider } from '@ethersproject/providers'
import { useEffect, useMemo, useState } from 'react'

import useActiveWeb3React from 'hooks/useActiveWeb3React'
import { findBestRobinhoodV2Route } from 'utils/robinhoodBestRoute'
import {
  BPS_DENOMINATOR,
  ROBINHOOD_MAINNET_V3_QUOTER,
  ROBINHOOD_PROTOCOL_FEE_BPS,
  ROBINHOOD_V3_FEE_TIERS,
} from 'config/robinhood/smartRouting'

const QUOTER_ABI = [
  'function quoteExactInputSingle((address tokenIn,address tokenOut,uint256 amountIn,uint24 fee,uint160 sqrtPriceLimitX96)) external returns (uint256 amountOut,uint160 sqrtPriceX96After,uint32 initializedTicksCrossed,uint256 gasEstimate)',
]

export type RobinhoodSmartRoute =
  | {
      kind: 'v2'
      router: string
      routerName: string
      path: string[]
      amountOut: BigNumber
      netAmountIn: BigNumber
    }
  | {
      kind: 'v3'
      fee: number
      amountOut: BigNumber
      netAmountIn: BigNumber
    }

interface QuoteExactInputSingleResult {
  amountOut: BigNumber
}

async function quoteV3(
  provider: Provider,
  tokenIn: string,
  tokenOut: string,
  amountIn: BigNumber,
  fee: number,
): Promise<BigNumber | null> {
  try {
    const quoter = new Contract(
      ROBINHOOD_MAINNET_V3_QUOTER,
      QUOTER_ABI,
      provider,
    )

    const result: QuoteExactInputSingleResult =
      await quoter.callStatic.quoteExactInputSingle({
        tokenIn,
        tokenOut,
        amountIn,
        fee,
        sqrtPriceLimitX96: 0,
      })

    if (!result.amountOut || result.amountOut.isZero()) {
      return null
    }

    return result.amountOut
  } catch {
    return null
  }
}

export async function findBestRobinhoodSmartRoute(
  provider: Provider,
  grossAmountIn: BigNumber,
  tokenIn: string,
  tokenOut: string,
): Promise<RobinhoodSmartRoute | null> {
  if (
    grossAmountIn.isZero() ||
    tokenIn.toLowerCase() === tokenOut.toLowerCase()
  ) {
    return null
  }

  const feeAmount = grossAmountIn
    .mul(ROBINHOOD_PROTOCOL_FEE_BPS)
    .div(BPS_DENOMINATOR)

  const netAmountIn = grossAmountIn.sub(feeAmount)

  if (netAmountIn.isZero()) {
    return null
  }

  const v2Promise = findBestRobinhoodV2Route(
    provider,
    netAmountIn,
    tokenIn,
    tokenOut,
  )

  const v3Promises = ROBINHOOD_V3_FEE_TIERS.map(async (fee) => {
    const amountOut = await quoteV3(
      provider,
      tokenIn,
      tokenOut,
      netAmountIn,
      fee,
    )

    return amountOut
      ? ({
          kind: 'v3',
          fee,
          amountOut,
          netAmountIn,
        } as RobinhoodSmartRoute)
      : null
  })

  const [v2Result, ...v3Results] = await Promise.all([
    v2Promise,
    ...v3Promises,
  ])

  const candidates: RobinhoodSmartRoute[] = []

  if (v2Result) {
    candidates.push({
      kind: 'v2',
      router: v2Result.router,
      routerName: v2Result.routerName,
      path: v2Result.path,
      amountOut: v2Result.amountOut,
      netAmountIn,
    })
  }

  v3Results.forEach((candidate) => {
    if (candidate) {
      candidates.push(candidate)
    }
  })

  return candidates.reduce<RobinhoodSmartRoute | null>(
    (best, candidate) =>
      !best || candidate.amountOut.gt(best.amountOut)
        ? candidate
        : best,
    null,
  )
}

export function useRobinhoodSmartRoute(
  amountIn: BigNumber | undefined,
  tokenIn: string | undefined,
  tokenOut: string | undefined,
) {
  const { chainId, library } = useActiveWeb3React()

  const [route, setRoute] = useState<RobinhoodSmartRoute | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const enabled = useMemo(
    () =>
      chainId === 4663 &&
      Boolean(
        library &&
          amountIn &&
          !amountIn.isZero() &&
          tokenIn &&
          tokenOut &&
          tokenIn.toLowerCase() !== tokenOut.toLowerCase(),
      ),
    [amountIn, chainId, library, tokenIn, tokenOut],
  )

  useEffect(() => {
    let cancelled = false

    if (!enabled || !library || !amountIn || !tokenIn || !tokenOut) {
      setRoute(null)
      setLoading(false)
      setError(null)

      return () => {
        cancelled = true
      }
    }

    const run = async () => {
      setLoading(true)
      setError(null)

      try {
        const best = await findBestRobinhoodSmartRoute(
          library,
          amountIn,
          tokenIn,
          tokenOut,
        )

        if (!cancelled) {
          setRoute(best)
          setLoading(false)
          setError(best ? null : 'No executable route found')
        }
      } catch (e) {
        if (!cancelled) {
          setRoute(null)
          setLoading(false)
          setError(
            e instanceof Error
              ? e.message
              : 'Route quote failed',
          )
        }
      }
    }

    run()

    return () => {
      cancelled = true
    }
  }, [amountIn, enabled, library, tokenIn, tokenOut])

  return {
    route,
    loading,
    error,
  }
}
